import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { useUnifiedAgentContext } from 'pages/chatbot/ui/UnifiedAgent/context/unifiedAgentContext'
import { KnowledgeBase } from '../KnowledgeBaseSection/KnowledgeBase'
import { AutoFollowUpSection } from '../AutoFollowUpSection/AutoFollowUpSection'
import { TriggerSection } from '../TriggerSection/TriggerSection'
import styles from './styles.module.scss'
import { InstructionsSection } from '../InstructionsSection/InstructionsSection'
import { BookingSection } from '../BookingSection/BookingSection'
import { SectionButtons } from './ui/SectionButtons'

export const Configure = observer(() => {
  const {
    validateTrigger,
    isLoadingSection,
    updateInstructions,
    resetEditSection,
    triggerSectionStore,
    instructionsSectionStore,
    bookingSectionStore,
    knowledgeBaseSectionStore,
  } = useUnifiedAgentContext()

  const refContent = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!validateTrigger || !refContent.current) return

    const firstError = refContent.current.querySelector('[data-error]')
    if (!(firstError instanceof HTMLElement)) return

    const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null
      const { overflowY } = getComputedStyle(element)
      return overflowY === 'scroll' || overflowY === 'auto'
        ? element
        : findScrollContainer(element.parentElement)
    }

    const scrollContainer = findScrollContainer(firstError.parentElement)
    if (!scrollContainer) return

    scrollContainer.scrollTo({
      top:
        scrollContainer.scrollTop +
        firstError.getBoundingClientRect().top -
        scrollContainer.getBoundingClientRect().top,
      behavior: 'smooth',
    })
  }, [validateTrigger])

  const hasTriggerError = !!(
    triggerSectionStore.sendFromError || triggerSectionStore.enrollmentError
  )
  const hasInstructionsError = !!instructionsSectionStore.error
  const hasKnowledgeBaseError =
    !!knowledgeBaseSectionStore.agentKnowledgeBaseStore.agentDocumentsStore.documentsError
  const hasBookingError = !!(
    bookingSectionStore.connectionError || bookingSectionStore.eventLinkError
  )

  return (
    <div ref={refContent} className={styles.wrap}>
      <div className={styles.section} data-error={hasTriggerError || undefined}>
        <TriggerSection />
      </div>
      <div className={styles.section} data-error={hasInstructionsError || undefined}>
        <InstructionsSection />
      </div>
      <div
        className={classnames(styles.section, styles.editableSection)}
        data-error={hasKnowledgeBaseError || undefined}
      >
        <KnowledgeBase />
        {knowledgeBaseSectionStore.hasChanges && (
          <SectionButtons
            isLoading={isLoadingSection}
            isDisabled={knowledgeBaseSectionStore.isLoading}
            onCancel={() => resetEditSection('knowledgeBase')}
            onSave={() => updateInstructions('knowledgeBase')}
          />
        )}
      </div>
      <div
        className={classnames(styles.section, styles.editableSection)}
        data-error={hasBookingError || undefined}
      >
        <BookingSection />
        {bookingSectionStore.hasChanges && (
          <SectionButtons
            onCancel={() => resetEditSection('booking')}
            onSave={() => updateInstructions('booking')}
            isLoading={isLoadingSection}
          />
        )}
      </div>
      <div className={styles.section}>
        <AutoFollowUpSection />
      </div>
    </div>
  )
})
